console.log('[App] 文件加载：App.tsx 已执行');

import { Component, useEffect, type ReactNode } from "react";
import Workspace4 from "./cockpit4/Workspace4";
import RuntimeManager from "./runtime/RuntimeManager";
import LoginPanel from "./cockpit4/components/LoginPanel";
import { useUserStore } from "./stores/userStore";
import { installVaultRoleFetchGuard } from "./utils/vaultAccessGuard";

// v6.23 权限：全局 vault-api 请求附加角色头（服务端按角色过滤敏感路径）
installVaultRoleFetchGuard();

console.log('[App] import 完成，RuntimeManager 类型:', typeof RuntimeManager);

// 在模块加载时就暴露到 window（不依赖 useEffect）
// 这样即使 useEffect 未执行，也能通过 window.RuntimeManager 诊断
if (typeof window !== 'undefined') {
  (window as any).RuntimeManager = RuntimeManager;
  console.log('[App] window.RuntimeManager 已设置（模块加载时）, initialized:', RuntimeManager.isInitialized());
}

console.log('[App] 准备定义 App 组件');

// ============================================
// ErrorBoundary - 捕获子组件渲染错误，避免白屏
// ============================================
class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[App ErrorBoundary] 捕获错误:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, color: '#ff6b6b', background: '#1a1a1a', minHeight: '100vh', fontFamily: 'monospace' }}>
          <h2 style={{ marginTop: 0 }}>XEAOS 渲染异常</h2>
          <p>子组件抛出了未捕获的错误，已被 ErrorBoundary 拦截：</p>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.error?.message || String(this.state.error)}
          </pre>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#888', marginTop: 16 }}>
            {this.state.error?.stack}
          </pre>
          <button
            onClick={() => location.reload()}
            style={{ marginTop: 16, padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            刷新页面
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================
// App 主组件
// ============================================
export default function App() {
  console.log('[App] App 组件正在渲染');

  // 登录认证：恢复会话
  const { currentUser, hydrated, hydrate } = useUserStore();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // 仅在已登录后才初始化 RuntimeManager（避免未登录时消耗资源）
  useEffect(() => {
    if (!currentUser) return;
    console.log('[App] currentUser 存在，开始初始化 RuntimeManager');
    // window.RuntimeManager 已在模块加载时设置
    RuntimeManager.initialize()
      .then(() => {
        console.log('[App] RuntimeManager 初始化成功 ✅, runtimeCount:', RuntimeManager.runtimeCount);
        console.log('[App] 已注册 Runtime:', RuntimeManager.getAllRuntimes());
        console.log('[App] GEO registered:', RuntimeManager.hasRuntime('geo' as any));
        console.log('[App] DSO registered:', RuntimeManager.hasRuntime('dso' as any));
        console.log('[App] GrowthFactory registered:', RuntimeManager.hasRuntime('growth_factory' as any));
      })
      .catch((error) => {
        console.error('[App] RuntimeManager 初始化失败 ❌:', error);
      });
  }, [currentUser]);

  // 未登录或会话未恢复完成时，显示登录界面
  if (!hydrated || !currentUser) {
    return (
      <AppErrorBoundary>
        <LoginPanel />
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
      <Workspace4 />
    </AppErrorBoundary>
  );
}
